<template lang="pug">
.outing-report-table-component
  .title 外出外泊届け

  //- テーブル
  b-table(
    :items="_value"
    :fields="fields"
    :busy="isLoading"
    sticky-header
    no-border-collapse
    table-class="basic-table"
    show-empty
  )
    //- ローディング
    template(#table-busy)
      .text-center.table-loading-message
        b-spinner.spinner
        span.message.align-self-center 読込み中...
    //- レコード無し
    template(#empty)
      .empty-message
        span 情報がありません

    //- セル：種別
    template(#cell(type)="row")
      .d-flex.align-items-center
        .type.center(v-if="row.item.type == 'goingOut'")
          span 外出届け
        .type.center(v-else)
          span 外泊届け
    //- セル：入力
    template(#cell(inputType)="row")
      .input-type.center(v-if="row.item.inputBy == 'family'") 本人
      .input-type.center(v-else) 代理
    //- セル：状態
    template(#cell(status)="row")
      .status.center.status-consented(v-if="row.item.consented == true") 承諾済
      .status.center.status-no-consented(v-else) 未承諾
    //- セル：日時
    template(#cell(datetime)="row")
      .d-flex.flex-column(v-if="row.item.type == 'goingOut'")
        .d-flex.flex-row
          span {{ getDate(row.item.startDatetime) }}（
          span(:class="weekCssClass(row.item.startDatetime)") {{ getWeek(row.item.startDatetime) }}）
          span {{ getTime(row.item.startDatetime) }}
          span ～
          span {{ getTime(row.item.endDatetime) }}
      .d-flex.flex-column(v-if="row.item.type == 'stayingOut'")
        .d-flex.flex-row
          span {{ getDate(row.item.startDatetime) }}（
          span(:class="weekCssClass(row.item.startDatetime)") {{ getWeek(row.item.startDatetime) }}）
          span {{ getTime(row.item.startDatetime) }}
          span ～
        .d-flex.flex-row
          span {{ getDate(row.item.endDatetime) }}（
          span(:class="weekCssClass(row.item.endDatetime)") {{ getWeek(row.item.endDatetime) }}）
          span {{ getTime(row.item.endDatetime) }}
    //- セル：利用者名（部屋名）
    template(#cell(resident)="row")
      span.resident {{ row.item.targetResidentInfo.name }}（{{ row.item.targetResidentInfo.roomName }}）
    //- セル：申請者（続柄）
    template(#cell(applicantFamily)="row")
      span.applicant-family {{ row.item.applicantFamilyInfo.name }}（{{ row.item.applicantFamilyInfo.relation }}）
    //- セル：お迎えに来る方（続柄）
    template(#cell(applicantPickup)="row")
      .d-flex.flex-column(v-if="row.item.applicantPickup == true")
        span 申請者本人
      .d-flex.flex-column(v-else)
        span {{ row.item.pickupPerson.name }}（{{ row.item.pickupPerson.relation }}）
</template>

<script lang="ts">
import { Component, Prop, Vue } from "vue-property-decorator";
import { FamilyInfo, InputBy, OutingType, TargetResidentInfo } from "@/commons/services/Outings";

/**
 * ### 活動履歴(面会予約)テーブルコンポーネント
 */
@Component({})
export default class OutingReportPreviewTable extends Vue {
  /** 1行あたりの情報配列 */
  @Prop({ required: true })
  value!: Array<OutingReportPreviewTableItem>;
  /** 列名配列 */
  @Prop({ required: true })
  fields!: Array<OutingReportPreviewTableField>;
  /** ローディング表示可否 */
  @Prop({ required: true })
  isLoading!: boolean;

  get _value(): Array<OutingReportPreviewTableItem> {
    return this.value;
  }

  /**
   * 日付(yyyy年mm月dd日)を返す
   * */
  public getDate(date: number): string {
    if (date) {
      return this.$dateUtil.convertNumberToString(date, "yyyy年mm月dd日");
    } else {
      return "";
    }
  }

  /**
   * 曜日を返す
   */
  public getWeek(date: number): string {
    if (date) {
      const dateObject: Date = this.$dateUtil.convertNumberToDate(date);
      return this.$dateUtil.getWeekName(dateObject, "ja");
    } else {
      return "";
    }
  }

  /**
   * 曜日の文字色CssClassの割り当て
   */
  public weekCssClass(date: number): string {
    let weekClassName: string = "";
    const weekName: string = this.getWeek(date);
    switch (weekName) {
      case "日":
        weekClassName = "sunday";
        break;
      case "土":
        weekClassName = "saturday";
        break;
      default:
        break;
    }

    return weekClassName;
  }

  /**
   * 時分を返す
   */
  public getTime(date: number): string {
    if (date) {
      return this.$dateUtil.convertNumberToString(date, "hh:MM");
    } else {
      return "";
    }
  }
}

/** テーブルの列名の情報 */
export interface OutingReportPreviewTableField {
  key: string;
  label: string;
  sortable: boolean;
  sortDirection: string;
  class?: string;
}

/** テーブルの1行あたりの情報 */
export interface OutingReportPreviewTableItem {
  /** 外出、外泊種別 */
  type: OutingType;
  /** 入力者 */
  inputBy: InputBy;
  /** 外出外泊開始日時 */
  startDatetime: number;
  /** 外出外泊終了日時 */
  endDatetime: number;
  /** 利用者グループ名 */
  residentGroupName: string;
  /** 外出外泊対象の利用者の申請時の情報 */
  targetResidentInfo: TargetResidentInfo;
  /** 申請した家族の申請時の情報 */
  applicantFamilyInfo: FamilyInfo;
  /** お迎え。申請者本人（true）、申請者以外（false） */
  applicantPickup: boolean;
  /** お迎え（自由記述） */
  pickupPerson: FamilyInfo | null | undefined;
  /** 主な外出場所 */
  location: string;
  /** 外出手段 */
  selectedVehicle: string | undefined;
  /** 施設での食事（開始日） */
  startDateNeedsMeal: string;
  /** 施設での食事（終了日） */
  endDateNeedsMeal: string;
}
</script>

<style lang="scss" scoped>
.outing-report-table-component {
  .title {
    font-size: 18px;
    font-weight: bold;
    margin-bottom: 4px;
  }
}
</style>
